import { v4 as uuid } from 'uuid';
import isEqual from 'lodash.isequal';

let blobUrls = {};
let fonts = {};

const downloadPathAsBlob = (path) =>
    fetch(path)
        .then((r) => r.blob())
        .then((blob) => blob.arrayBuffer())
        .then((buff) => URL.createObjectURL(new Blob([buff])));

// Image is scaled so that all the canvas pixels will be covered by the image
const scaleToFillAndDraw = (canvas, ctx, img) => {
    const scale = Math.max(canvas.width / img.width, canvas.height / img.height);

    // Get the width and height of the image
    const imgWidth = Math.round(img.width * scale);
    const imgHeight = Math.round(img.height * scale);

    // Get the top left position of the image
    const x = (canvas.width / 2) - (img.width / 2) * scale;
    const y = (canvas.height / 2) - (img.height / 2) * scale;

    ctx.drawImage(img, x, y, imgWidth, imgHeight);
}

const loadImage = (blob) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = blob;
});

const loadFont = (font, retry = 50) => new Promise((resolve) => {
    if (retry === 0) {
        console.error(`Can't load custom font`, font);
        resolve(null);
    }
    const id = uuid().replaceAll('-', '');
    const newFont = new FontFace(id, `url(${font})`);
    document.fonts.add(newFont);
    newFont.loaded.then(() => resolve(id)).catch(() => { });
    document.fonts.load(`12px ${newFont.family}`).catch(() => {
        loadFont(font, retry - 1).then((newId) => resolve(newId));
    });
});

const resizeWidth = (ctx, line, size, padding, maxWidth) => {
    let newSize = size;
    while (ctx.measureText(line).width + padding * 2 > maxWidth) {
        newSize = newSize - 2 < 2 ? 2 : newSize - 2;
        ctx.font = ctx.font.replace(/\d+px/, `${newSize}px`);
        if (newSize === 2) break;
    }
    return newSize;
}

const resizeHeight = (ctx, words, size, padding, maxWidth, maxHeight) => {
    let newSize = size;
    let tm = measureTextWrapped(ctx, words, padding, maxWidth);
    while (tm.height > maxHeight - padding) {
        newSize = newSize - 2 < 2 ? 2 : newSize - 2;
        ctx.font = ctx.font.replace(/\d+px/, `${newSize}px`);
        if (newSize === 2) break;
        tm = measureTextWrapped(ctx, words, padding, maxWidth);
    }
    return newSize;
}

// Implementation of ctx.measureText, adds pixel detection and return the text drawed in the middle of the tmpCanvas
const measureText = (ctx, text, size, padding, maxWidth, boxHeight) => {
    const width = maxWidth;
    const height = Math.round(boxHeight * 1.10);

    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = width;
    tmpCanvas.height = height;

    const tmpCtx = tmpCanvas.getContext('2d');
    tmpCtx.clearRect(0, 0, width, height);

    tmpCtx.font = ctx.font;
    tmpCtx.fillStyle = ctx.fillStyle;
    tmpCtx.textAlign = ctx.textAlign;
    tmpCtx.textBaseline = 'top';

    // Determine the coordinates of the anchor point to draw the text according to the box
    let posX = 0 + padding / 2;
    if (tmpCtx.textAlign === 'center') {
        posX = 0 + width / 2;
    } else if (['right', 'end'].includes(tmpCtx.textAlign)) {
        posX = maxWidth - padding / 2;
    }

    wrapText(tmpCtx, text, posX, boxHeight / 10, maxWidth, size, padding)

    const { data } = tmpCtx.getImageData(0, 0, width, height);
    let top = null;
    let right = null;
    let bottom = null;
    let left = null;

    let x = 0;
    let y = 0;

    // Find the top most non-white pixel
    while (!top && y++ < height) {
        for (let x = 0; x < width; x++) {
            // Read the alpha value (+3) on line y at the column x
            if (data[y * width * 4 + x * 4 + 3]) {
                top = y;
                break;
            }
        }
    }

    // Find the bottom most non-white pixel
    y = height;
    while (!bottom && y--) {
        for (let x = 0; x < width; x++) {
            // Read the alpha value (+3) on line y at the column x
            if (data[y * width * 4 + x * 4 + 3]) {
                bottom = y;
                break;
            }
        }
    }

    // Find the left most non-white pixel
    x = 0;
    while (!left && x++ < width) {
        for (let y = top; y <= bottom; y++) {
            // Read the alpha value (+3) on line y at the column x
            if (data[y * width * 4 + x * 4 + 3]) {
                left = x;
                break;
            }
        }
    }

    // Find the right most non-white pixel
    x = width;
    while (!right && x--) {
        for (let y = top; y <= bottom; y++) {
            // Read the alpha value (+3) on line y at the column x
            if (data[y * width * 4 + x * 4 + 3]) {
                right = x;
                break;
            }
        }
    }

    return {
        textHeight: bottom - top + 2,
        textWidth: right - left + 2,
        tmpCanvas,
        top: top - 1,
        left: left - 1
    }
}

const measureTextWrapped = (ctx, words, padding, maxWidth) => {
    let line = '';
    const lines = [];

    for (let i = 0; i < words.length; i++) {
        const testLine = i === 0 ? words[0] : `${line} ${words[i]}`;
        const metrics = ctx.measureText(testLine);
        if (metrics.width + padding * 2 > maxWidth && i > 0) {
            lines.push(ctx.measureText(line));
            line = words[i];
        } else {
            line = testLine;
        }
    }
    const metrics = ctx.measureText(line);
    lines.push(metrics);
    return {
        lines,
        width: Math.max(...lines.map((line) => line.width)),
        height: lines.reduce((acc, line) => {
            const actualBoundingBoxAscent = line.actualBoundingBoxAscent > 0 ? line.actualBoundingBoxAscent : 0;
            const actualBoundingBoxDescent = line.actualBoundingBoxDescent > 0 ? line.actualBoundingBoxDescent : 0;
            return acc + actualBoundingBoxAscent + actualBoundingBoxDescent + 10;
        }, 0),
    };
}

const textResizer = (ctx, text, maxWidth, maxHeight, padding) => {
    // Uppercase handling
    const value = text.uppercase ? text.value.toUpperCase() : text.value;

    if (!value) { return; }

    // Trim the text value
    const words = value.split(' ');

    ctx.textBaseline = 'top';
    let { size } = text;
    let line = '';

    // Makes the text fit in maxHeight and maxWidth on multiple lines
    for (let i = 0; i < words.length; i++) {
        const testLine = i === 0 ? words[0] : `${line} ${words[i]}`;
        const metrics = ctx.measureText(testLine);
        // If the current line with the next word is longer than maxWidth
        if (metrics.width + padding * 2 > maxWidth && i > 0) {
            // Resize the text if the width of the current line is still longer than maxWidth
            size = resizeWidth(ctx, line, size, padding, maxWidth);
            line = words[i];
        } else {
            line = testLine;
        }
    }

    // Resize the text if the height of all the lines is larger than maxHeight
    size = resizeHeight(ctx, words, size, padding, maxWidth, maxHeight);

    // Resize the text if the width of the last line is still longer than maxWidth
    size = resizeWidth(ctx, line, size, padding, maxWidth);

    return size;
}

// Draw the text
const wrapText = (ctx, text, x, y, maxWidth, lineHeight, padding) => {
    const words = text.split(' ');
    let line = '';

    for (let i = 0; i < words.length; i++) {
        const testLine = i === 0 ? words[0] : `${line} ${words[i]}`;
        const metrics = ctx.measureText(testLine);
        if (metrics.width + padding > maxWidth && i > 0) {
            ctx.fillText(line, x, y);
            line = words[i];
            y += lineHeight;
        } else if (testLine !== ' ') {
            line = testLine;
        }
    }
    ctx.fillText(line, x, y);
}

const drawText = async (ctx, text, fonts) => {
    if (text.box && text.box.display) {
        ctx.beginPath();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0, 0, 255, 1)';
        ctx.setLineDash([5]);
        ctx.rect(text.box.x, text.box.y, text.box.sizeX, text.box.sizeY);
        ctx.stroke();
    }

    const value = text.uppercase ? text.value.toUpperCase() : text.value;

    if (text.font) {
        ctx.font = `${text.italic ? 'italic ' : ''}${text.bold ? 'bold ' : ''}${text.size}px ${fonts[text.font]}`;
    } else {
        ctx.font = `${text.italic ? 'italic ' : ''}${text.bold ? 'bold ' : ''}${text.size}px Open Sans, sans-serif`;
    }

    ctx.textAlign = text.align || 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = text.color || '#000000';

    const padding = (text?.highlight?.padding || 0);

    // Get the perfet size of the text to wrapped it inside the box
    const size = textResizer(ctx, text, text.box.sizeX, text.box.sizeY, padding);

    // Call measureText to draw the text on tmpCanvas and return metrics
    const { top, left, textHeight, textWidth, tmpCanvas } = measureText(ctx, value, size, padding, text?.box?.sizeX, text?.box?.sizeY);

    // Determine the coordinates of the anchor point to draw the text according to the box
    let posX = (text?.box?.x || 0) + padding / 2;
    if (text.align === 'center') {
        posX = (text?.box?.x || 0) + (text?.box?.sizeX - textWidth) / 2;
    } else if (['right', 'end'].includes(text.align)) {
        posX = (text?.box?.x || 0) + text.box.sizeX - padding / 2 - textWidth;
    }

    let posY = (text?.box?.y || 0) + (text?.box?.sizeY - textHeight) / 2;

    const middleBoxY = text?.box?.y + text?.box?.sizeY / 2;

    if (text?.flag === 'MIDDLE_TOP') {
        posX = (ctx.canvas.width - textWidth) / 2;
        posY = (ctx.canvas.height - padding) / 2 - textHeight;
    } else if (text?.flag === 'MIDDLE_BOTTOM') {
        posX = (ctx.canvas.width - textWidth) / 2;
        posY = (ctx.canvas.height + padding) / 2;
    }

    if (text.value !== '' && (text.strikethrough || text.underline || text.highlight)) {
        // Highliting
        if (text.highlight) {
            const { opacity, color } = text.highlight;
            ctx.beginPath();
            ctx.fillStyle = color;
            ctx.globalAlpha = opacity;
            const highlightPosX = textWidth + padding * 2 > text?.box?.sizeX ? text?.box?.x : posX - padding / 2;
            const highlightSizeX = textWidth + padding * 2 > text?.box?.sizeX ? text?.box?.sizeX : textWidth + padding;
            const highlightPosY = textHeight + padding > text?.box?.sizeY ? text?.box?.y : middleBoxY - textHeight / 2 - padding / 2;
            const highlightSizeY = textHeight + padding > text?.box?.sizeY ? text?.box?.sizeY : textHeight + padding;

            if (text?.flag === 'MIDDLE_TOP' || text?.flag === 'MIDDLE_BOTTOM') {
                ctx.fillRect(highlightPosX, posY - padding / 2, highlightSizeX, highlightSizeY);
            } else {
                ctx.fillRect(highlightPosX, highlightPosY, highlightSizeX, highlightSizeY);
            }

            // Reset canal alpha
            ctx.globalAlpha = 1;
        }
    }

    // Paste text on tmpCanvas into canvas
    ctx.drawImage(tmpCanvas, left, top, textWidth, textHeight, posX, posY, textWidth, textHeight);
    return { text: value, textWidth, textHeight };
}

const dedupAndLoadFonts = (urls) => (
    Promise.all([...new Set(urls)].map((url) => loadFont(url)
        .then((id) => ({
            [url]: id
        }))))
        .then((result) => result.reduce((acc, elem) => ({ ...acc, ...elem }), []))
);

const renderer = async (canvas, template) => {
    const ctx = canvas.getContext('2d');

    // Downloading background image as blobUrl if needed
    if (template?.path && !blobUrls[template.path]) {
        const blob = await downloadPathAsBlob(template.path);
        blobUrls = { ...blobUrls, [template.path]: blob };
    }

    // Create an array of unloaded fonts
    const newFonts = template?.texts?.filter((text) => text.font && !fonts[text.font]).map((text) => text.font);

    // Downloading and dedup fonts
    fonts = {
        ...fonts,
        ...await dedupAndLoadFonts(newFonts),
    };

    // Loading the background
    let img = null;
    if (blobUrls[template?.path]) {
        img = await loadImage(blobUrls[template.path]);
    }

    // Clearing
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Drawing the background
    if (img) {
        scaleToFillAndDraw(canvas, ctx, img);
    }

    // Drawing the videoBox
    if (template?.videoBox?.display) {
        ctx.beginPath();
        ctx.rect(template.videoBox.x, template.videoBox.y, template.videoBox.sizeX, template.videoBox.sizeY);
        ctx.fillStyle = 'rgba(35, 255, 35, 0.5)';
        ctx.fill();
    }

    // Drawing texts and return metrics
    if (template?.texts) {
        return Promise.all(template?.texts?.map((text) => drawText(ctx, text, fonts)));
    }
    return null;
};

export const verifAlpha = (canvasRef, videoBox) => {
    let valid = true;
    const ctx = canvasRef.getContext('2d');
    const imgData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
    for (let i = 0; i < imgData.data.length; i += 4) {
        const x = (i / 4) % imgData.width;
        const y = Math.floor(i / 4 / imgData.width);
        if ((x < videoBox.x || x > videoBox.x + videoBox.sizeX || y < videoBox.y || y > videoBox.y + videoBox.sizeY) && imgData.data[i + 3] !== 255) {
            valid = false;
            break;
        }
    }
    return valid;
}

export default (canvas, template) => renderer(canvas, template);